import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Vector3 } from 'three';

import { CalculatedLine } from '@paragon/contract-library';
import { Direction2D, Point2D, rightAngle } from '@paragon/geometry-library';
import { calculateLine } from '@paragon/relative-geometry';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { updateFeatures } from '../../store/actions/appActions';
import { nudgeReferenceLine } from '../../store/actions/editorActions';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { isPickingActive } from '../../store/selectors/pickingSelectors';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../util/events/types';
import { NudgeHandle } from './NudgeHandle';

export const OffsetReferenceLineHandle = ({ line }: { line: CalculatedLine }) => {
  const dispatch = useAppDispatch();

  const activeComponentDesign = useAppSelector(getActiveComponentDesign)!;
  const isPickingObject = useAppSelector(isPickingActive);
  const asyncRequests = useAppSelector((state) => state.asyncRequests);
  const asyncRequestForThisComponentDesign = asyncRequests[activeComponentDesign.guid];
  const isAsyncRequestInterfering =
    !!asyncRequestForThisComponentDesign && asyncRequestForThisComponentDesign.type !== 'updateFeatures';
  const lineIsOffsettable = line.lineType === 'Parallel' || line.lineType === 'Perpendicular';

  const line2D = calculateLine(line, activeComponentDesign.component);

  const lastMousePosition = useRef(line2D?.basePoint ?? Point2D.origin);
  const projectedPoint = line2D ? lastMousePosition.current.projectOntoLine(line2D) : lastMousePosition.current;
  const position = new Vector3(projectedPoint.x, projectedPoint.y);

  const coordinateUpDirection = line2D?.direction ?? Direction2D.up;
  const { lineType, guid: lineGuid } = line;
  const moveCallBack = useCallback(
    (shiftX: number, shiftY: number) => {
      if (lineType !== 'Parallel' && lineType !== 'Perpendicular') return;
      const distance = new Point2D(shiftX, shiftY).getCoordinate(
        Direction2D.fromObject(coordinateUpDirection).rotate(rightAngle),
      );
      dispatch(nudgeReferenceLine(lineGuid, distance));
    },
    [dispatch, lineType, lineGuid, coordinateUpDirection],
  );

  const invalidate = useInvalidate();
  const mouseMove = useCallback(
    (event: GlobalThreeJSDomObjectEvent) => {
      /*
       * Why is lastMousePosition managed with useRef instead of useState?
       *
       * Setting a ref and triggering a re-render is subtly different from setting state. When re-rendering, React can
       * batch state updates, but it does not batch them with updates to the store (accessed via useAppSelector). This
       * become an issue when a single event can trigger both kinds of updates, which is exactly what mouse movement
       * does here. The mouse controls the position of the visual along its line, and when dragging, updates the line in
       *  the store. The store update (line position) is applied first, then the state update (projecting from the
       * mouse). Without any effort to apply these updates together, this leads to jerky visual effects which are most
       * noticeable when dragging the visual at 45° from its line: the visual follows a stair-stepped path.
       *
       * By tracking the necessary values with refs instead, we make their freshest values available during the store-
       * update-triggered render.
       *
       * Lastly, invalidate, which triggers a useState-based update, ensures that a re-render is still triggered even
       * when not dragging.
       */
      lastMousePosition.current = Point2D.fromObject(event.eventPosition.worldPosition);
      invalidate();
    },
    [invalidate],
  );

  useEffect(() => {
    threeJsDom.addGlobalListener('mousemove', mouseMove);
    return () => threeJsDom.removeGlobalListener('mousemove', mouseMove);
  }, [mouseMove]);

  if (isPickingObject || isAsyncRequestInterfering || !lineIsOffsettable || !line2D) return null;

  return (
    <NudgeHandle
      editType="EditingReferenceLineOffset"
      leftRight
      moveCallback={moveCallBack}
      postEditCallback={() => dispatch(updateFeatures())}
      position={position}
      size={8}
      coordinateUpDirection={coordinateUpDirection}
      settingsButton={{ type: 'Snapping', position: { x: 9, y: 0, z: 0 }, size: 3 }}
    />
  );
};

const useInvalidate = () => {
  const [_invalidateTracker, setInvalidateTracker] = useState(0);
  return useCallback(() => setInvalidateTracker((s) => s + 1), []);
};
